/**
 * Nuture Cart Upsell
 *
 * Web component do carrossel de upsells no carrinho, consumindo os cards
 * renderizados server-side a partir dos metaobjects `checkout_upsell` do
 * Rafobase (mesma fonte da extension de checkout).
 *
 * - Ordena por priority; navega ‹ 1/N ›.
 * - Upsell `recurring` só aparece com assinatura no carrinho (nada pra herdar
 *   de _frequency); `choice` sem assinatura vira compra única, sem checkbox.
 * - Upsell já adicionado sai de cena: remover é pela linha do carrinho, que
 *   tem o Remover nativo. Se a linha sai, o card volta sozinho.
 * - Add/remove com o contrato exato da extension de checkout:
 *   `_upsell: "true"`; avulso com desconto → `_plan_price` + "Entrega: Apenas
 *   no 1º pedido"; recorrente → variante publicada (preço com desconto no CMS,
 *   correto pro PagBrasil) herdando `_frequency` + "Frequencia da assinatura"
 *   das linhas do carrinho.
 * - Atualiza o drawer do Horizon via `shopify:cart:lines-update` (mesmo padrão
 *   do RafobaseCart.refreshThemeCart — sem reload).
 *
 * O re-render do drawer (morph) reaplica o HTML server-side — root e cards
 * voltam com `hidden` e o CTA volta ao label original. Por isso: eventos
 * delegados no root (sobrevivem à troca dos filhos) + MutationObserver que
 * re-sincroniza; todos os writes são guardados por igualdade pra não gerar
 * novas mutações em loop.
 */

const FREQUENCY_DAYS = { S: 7, Q: 15, M: 30, B: 60, T: 90 };

class RfCartUpsell extends HTMLElement {
  constructor() {
    super();
    this._busy = false;
    this._cart = null;
    this._idx = 0;
    this._errorTimer = null;
    this._refreshTimer = null;
    this._onCartUpdate = () => this._scheduleRefresh();
  }

  connectedCallback() {
    this.addEventListener('click', (e) => {
      const cta = e.target.closest('[data-upsell-cta]');
      if (cta) return this._handleCta(cta.closest('[data-upsell-card]'));
      if (e.target.closest('[data-upsell-prev]')) return this._step(-1);
      if (e.target.closest('[data-upsell-next]')) return this._step(1);
    });
    this.addEventListener('change', (e) => {
      if (e.target.closest('[data-upsell-recurring]')) {
        this._renderCard(e.target.closest('[data-upsell-card]'));
      }
    });

    this._observer = new MutationObserver(() => this._scheduleRefresh());
    this._observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });

    document.addEventListener('shopify:cart:lines-update', this._onCartUpdate);
    this._refresh();
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this._onCartUpdate);
    this._observer?.disconnect();
    clearTimeout(this._errorTimer);
    clearTimeout(this._refreshTimer);
  }

  _scheduleRefresh() {
    clearTimeout(this._refreshTimer);
    this._refreshTimer = setTimeout(() => this._refresh(), 80);
  }

  async _refresh() {
    const rootUrl = window.Shopify?.routes?.root || '/';
    try {
      const res = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this._cart = await res.json();
    } catch (err) {
      console.warn('[rf-cart-upsell] Falha ao ler cart.js:', err);
      return;
    }
    this._render();
  }

  _allCards() {
    return [...this.querySelectorAll('[data-upsell-card]')].sort(
      (a, b) => Number(a.dataset.priority || 0) - Number(b.dataset.priority || 0),
    );
  }

  /** Primeira linha de assinatura do carrinho: { frequency, label } | null */
  _cartFrequency() {
    for (const item of this._cart?.items || []) {
      const freq = item.properties?._frequency;
      if (freq) {
        return { frequency: freq, label: item.properties?.['Frequencia da assinatura'] || null };
      }
    }
    return null;
  }

  /** Linha do carrinho adicionada como este upsell (variante original ou publicada) */
  _upsellCartLine(card) {
    const ids = [Number(card.dataset.variantId)];
    if (card.dataset.publishedVariantId) ids.push(Number(card.dataset.publishedVariantId));
    return (this._cart?.items || []).find((item) => ids.includes(item.id) && item.properties?._upsell === 'true');
  }

  _eligibleCards() {
    const hasSubscription = !!this._cartFrequency();
    return this._allCards().filter(
      (card) =>
        // já está no carrinho: a linha tem o Remover nativo, o card sai de cena
        !this._upsellCartLine(card) && (card.dataset.deliveryMode !== 'recurring' || hasSubscription),
    );
  }

  _isRecurring(card) {
    const mode = card.dataset.deliveryMode;
    if (mode === 'recurring') return true;
    if (mode === 'choice') {
      const checkbox = card.querySelector('[data-upsell-recurring]');
      return !!(checkbox && !checkbox.closest('[data-upsell-choice]').hidden && checkbox.checked);
    }
    return false;
  }

  /** Writes guardados por igualdade — não geram mutação quando nada mudou */
  _setHidden(el, value) {
    if (el && el.hidden !== value) el.hidden = value;
  }

  _setText(el, value) {
    if (el && el.textContent !== value) el.textContent = value;
  }

  _render() {
    if (!this._cart) return;

    const eligible = this._eligibleCards();
    if (!eligible.length) {
      this._setHidden(this, true);
      return;
    }
    this._setHidden(this, false);

    this._idx = Math.min(this._idx, eligible.length - 1);
    for (const card of this._allCards()) {
      this._setHidden(card, card !== eligible[this._idx]);
    }
    this._renderCard(eligible[this._idx]);

    const headingEl = this.querySelector('[data-upsell-heading]');
    const override = this.dataset.headingOverride?.trim();
    this._setText(
      headingEl,
      override || `Você tem ${eligible.length} ${eligible.length === 1 ? 'oferta exclusiva' : 'ofertas exclusivas'}`,
    );

    const nav = this.querySelector('[data-upsell-nav]');
    if (nav) {
      this._setHidden(nav, eligible.length < 2);
      this._setText(nav.querySelector('[data-upsell-count]'), `${this._idx + 1}/${eligible.length}`);
      nav.querySelector('[data-upsell-prev]').disabled = this._idx === 0;
      nav.querySelector('[data-upsell-next]').disabled = this._idx >= eligible.length - 1;
    }
  }

  _renderCard(card) {
    const hasSubscription = !!this._cartFrequency();

    const choice = card.querySelector('[data-upsell-choice]');
    if (choice) {
      this._setHidden(choice, card.dataset.deliveryMode !== 'choice' || !hasSubscription);
    }

    const delivery = card.querySelector('[data-upsell-delivery]');
    if (delivery) {
      let text = 'Entrega: Apenas no 1º pedido';
      if (this._isRecurring(card)) {
        const days = FREQUENCY_DAYS[this._cartFrequency()?.frequency];
        text = days ? `Entrega: A cada ${days} dias` : 'Entrega: Recorrente';
      }
      this._setText(delivery, text);
    }
  }

  _step(delta) {
    const eligible = this._eligibleCards();
    this._idx = Math.max(0, Math.min(this._idx + delta, eligible.length - 1));
    this._render();
  }

  async _handleCta(card) {
    if (this._busy || !card) return;
    this._busy = true;
    card.setAttribute('data-busy', '');

    try {
      const line = this._upsellCartLine(card);
      if (line) {
        await this._remove(line);
      } else {
        await this._add(card);
      }
      await this._refresh();
      this._notifyTheme();
    } catch (err) {
      console.error('[rf-cart-upsell] Falha:', err);
      this._showError('Não foi possível atualizar. Tente novamente.');
    } finally {
      card.removeAttribute('data-busy');
      this._busy = false;
    }
  }

  async _add(card) {
    const recurring = this._isRecurring(card);
    const publishedId = Number(card.dataset.publishedVariantId) || null;
    const usePublished = recurring && publishedId;

    const properties = { _upsell: 'true' };

    const pct = Number(card.dataset.discountPct) || 0;
    if (!usePublished && pct > 0) {
      const originalPrice = Number(card.dataset.originalCents) / 100;
      properties._plan_price = (originalPrice * (1 - pct / 100)).toFixed(2);
    }

    if (recurring) {
      const freq = this._cartFrequency();
      if (freq) {
        properties._frequency = freq.frequency;
        if (freq.label) properties['Frequencia da assinatura'] = freq.label;
      }
      const days = FREQUENCY_DAYS[freq?.frequency];
      properties['Entrega'] = days ? `A cada ${days} dias` : 'Recorrente';
    } else {
      properties['Entrega'] = 'Apenas no 1º pedido';
    }

    const rootUrl = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${rootUrl}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: usePublished ? publishedId : Number(card.dataset.variantId), quantity: 1, properties }],
      }),
    });
    if (!res.ok) throw new Error(`cart/add.js HTTP ${res.status}`);
  }

  async _remove(line) {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${rootUrl}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: 0 }),
    });
    if (!res.ok) throw new Error(`cart/change.js HTTP ${res.status}`);
  }

  /** Mesmo padrão do RafobaseCart.refreshThemeCart: o cart-items-component do
   *  Horizon re-busca o carrinho sozinho quando o evento vem sem sections. */
  _notifyTheme() {
    try {
      const evt = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
      evt.action = 'update';
      evt.promise = Promise.resolve({ detail: { source: 'nuture-cart-upsell' } });
      document.dispatchEvent(evt);
    } catch {
      /* refresh é cosmético */
    }
  }

  _showError(message) {
    const errorEl = this.querySelector('[data-upsell-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.classList.remove('hidden');
    clearTimeout(this._errorTimer);
    this._errorTimer = setTimeout(() => {
      errorEl.classList.add('hidden');
      errorEl.textContent = '';
    }, 4000);
  }
}

if (!customElements.get('rf-cart-upsell')) {
  customElements.define('rf-cart-upsell', RfCartUpsell);
}
